import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Clock, BookOpen, PlayCircle, ShoppingBag, ExternalLink, Loader2 } from "lucide-react";
import { slugify } from "@/lib/slug";

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [owned, setOwned] = useState(false);
  const [showPlan, setShowPlan] = useState(false);
  const { addItem, has } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setP(r.data));
  }, [id]);

  useEffect(() => {
    if (user && id) {
      api.get(`/library/owns/${id}`).then((r) => setOwned(r.data.owned)).catch(() => {});
    } else { setOwned(false); }
  }, [user, id]);

  if (!p) {
    return <Layout><div className="container-narrow py-32 overline text-brand-inkMuted">Loading…</div></Layout>;
  }

  const credit = p.author || p.instructor;
  const isCourse = p.type === "course";
  const totalLessons = (p.modules || []).reduce((s, m) => s + (m.lessons?.length || 0), 0);

  const handleAdd = () => {
    addItem(p);
    toast.success("Added to cart");
  };

  return (
    <Layout>
      <SEO
        title={p.title}
        description={p.description ? String(p.description).slice(0, 160) : undefined}
      />
      <section className="container-narrow py-12 md:py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <Link to={isCourse ? "/courses" : "/books"} className="overline text-brand-inkMuted hover:text-brand-ink" data-testid="back-to-catalog">
            ← Back to {isCourse ? "courses" : "books"}
          </Link>
          <div className="mt-6 aspect-[16/11] bg-brand-bg overflow-hidden border border-brand-line">
            <img
              src={p.cover_image}
              alt={p.title}
              className="w-full h-full object-cover"
              style={{ objectPosition: (p.cover_image || "").includes("forged%20in%20fire") ? "right center" : "center" }}
            />
          </div>

          {/* description */}
          <div className="mt-12">
            <span className="overline text-brand-rust">About this {p.type}</span>
            <p className="font-serif text-2xl md:text-3xl mt-4 text-brand-ink leading-snug" data-testid="product-description">
              {p.description}
            </p>
          </div>

          {/* Based on the book (courses only) */}
          {isCourse && p.based_on_book && (
            <Link
              to={`/p/${p.based_on_book.product_id}`}
              data-testid="based-on-book-card"
              className="mt-12 group flex items-center gap-5 bg-white border border-brand-line p-5 hover:-translate-y-0.5 hover:shadow-md transition-all duration-300"
            >
              <div className="w-20 h-24 flex-none bg-brand-bg overflow-hidden">
                <img src={p.based_on_book.cover_image} alt={p.based_on_book.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="flex-1">
                <span className="overline text-brand-rust">Based on the book</span>
                <p className="font-serif text-xl text-brand-ink mt-1 leading-snug">{p.based_on_book.title}</p>
                <p className="text-sm text-brand-inkMuted">by {p.based_on_book.author}</p>
              </div>
              <span className="overline text-brand-rust group-hover:translate-x-1 transition-transform">Read the book →</span>
            </Link>
          )}

          {/* Companion course (books only) */}
          {!isCourse && p.companion_course && (
            <Link
              to={`/p/${p.companion_course.product_id}`}
              data-testid="companion-course-card"
              className="mt-12 group flex items-center gap-5 bg-white border border-brand-line p-5 hover:-translate-y-0.5 hover:shadow-md transition-all duration-300"
            >
              <div className="w-20 h-24 flex-none bg-brand-bg overflow-hidden">
                <img src={p.companion_course.cover_image} alt={p.companion_course.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="flex-1">
                <span className="overline text-brand-rust">Companion course</span>
                <p className="font-serif text-xl text-brand-ink mt-1 leading-snug">{p.companion_course.title}</p>
                <p className="text-sm text-brand-inkMuted">with {p.companion_course.instructor} · ${Number(p.companion_course.price).toFixed(0)}</p>
              </div>
              <span className="overline text-brand-rust group-hover:translate-x-1 transition-transform">Take the course →</span>
            </Link>
          )}

          {/* book chapters preview */}
          {!isCourse && p.preview_chapters?.length > 0 && (
            <div className="mt-12">
              <span className="overline text-brand-rust">Table of Contents</span>
              <ul className="mt-5 divide-y divide-brand-line border border-brand-line">
                {p.preview_chapters.map((c, i) => (
                  <li key={i} className="flex items-center gap-4 px-5 py-4">
                    <span className="overline text-brand-inkMuted">0{i + 1}</span>
                    <span className="text-brand-ink">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* course curriculum */}
          {isCourse && (
            <div className="mt-12">
              <span className="overline text-brand-rust">Curriculum</span>
              <div className="mt-5 space-y-6">
                {(p.modules || []).map((m, mi) => {
                  const lessons = m.lessons || [];
                  const recorded = lessons.filter(
                    (l) => l.video_url && !l.video_url.includes("dQw4w9WgXcQ"),
                  ).length;
                  const fullyRecorded = lessons.length > 0 && recorded === lessons.length;
                  return (
                    <div key={m.module_id} className="border border-brand-line bg-white">
                      <div className="px-6 py-4 border-b border-brand-line flex items-center justify-between gap-3 flex-wrap">
                        <span className="font-serif text-lg text-brand-ink">{m.title}</span>
                        <div className="flex items-center gap-3">
                          <span
                            data-testid={`module-progress-${mi}`}
                            className={`text-xs uppercase tracking-[.14em] px-2 py-0.5 ${
                              fullyRecorded
                                ? "bg-brand-rust/10 text-brand-rust"
                                : "bg-brand-bg text-brand-inkMuted"
                            }`}
                          >
                            {recorded} / {lessons.length} recorded
                          </span>
                          <span className="overline text-brand-inkMuted">{lessons.length} lessons</span>
                        </div>
                      </div>
                      <ul className="divide-y divide-brand-line">
                        {lessons.map((l) => {
                          const hasVideo = l.video_url && !l.video_url.includes("dQw4w9WgXcQ");
                          return (
                            <li key={l.lesson_id} className="px-6 py-3 flex items-center justify-between text-sm">
                              <span className="flex items-center gap-3 text-brand-ink">
                                <PlayCircle className={`w-4 h-4 ${hasVideo ? "text-brand-rust" : "text-brand-inkMuted/50"}`} />
                                {l.title}
                                {!hasVideo && (
                                  <span className="text-xs uppercase tracking-[.12em] text-brand-inkMuted/70 ml-1">— coming soon</span>
                                )}
                              </span>
                              <span className="text-brand-inkMuted flex items-center gap-1"><Clock className="w-3 h-3" /> {l.duration}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* About the author / instructor */}
          {(p.creator_bio || p.creator_picture) && (
            <div className="mt-16 pt-12 border-t border-brand-line" data-testid="creator-section">
              <span className="overline text-brand-rust">About the {isCourse ? "instructor" : "author"}</span>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-6 items-start">
                {p.creator_picture && (
                  <div className="w-40 h-40 bg-brand-bg overflow-hidden border border-brand-line">
                    <img src={p.creator_picture} alt={credit} className="w-full h-full object-cover" />
                  </div>
                )}
                <div>
                  <Link to={`/author/${slugify(credit)}`} data-testid="creator-name-link">
                    <h3 className="font-display text-2xl md:text-3xl text-brand-ink leading-snug hover:text-brand-rust transition-colors" data-testid="creator-name">{credit}</h3>
                  </Link>
                  {p.creator_bio && <p className="mt-3 text-brand-inkMuted leading-relaxed" data-testid="creator-bio">{p.creator_bio}</p>}
                  <Link to={`/author/${slugify(credit)}`} className="inline-block mt-4 overline text-brand-rust hover:text-brand-ink transition-colors">
                    See all by {credit} →
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT — sticky info */}
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-28 bg-white border border-brand-line p-8">
            {isCourse && (
              <div className="aspect-video bg-black mb-6 -mx-8 -mt-8" data-testid="product-trailer">
                <iframe
                  title="Course trailer"
                  src="https://www.youtube.com/embed/0tQwsSac9D8"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
            <span className="overline text-brand-rust">{p.category}</span>
            <h1 className="font-display text-4xl md:text-5xl mt-3 leading-[1.05] text-brand-ink" data-testid="product-title">{p.title}</h1>
            {p.subtitle && <p className="text-brand-inkMuted mt-2">{p.subtitle}</p>}
            <div className="mt-6 flex items-center gap-3 overline text-brand-inkMuted">
              <span>by <Link to={`/author/${slugify(credit)}`} data-testid="product-credit-link" className="text-brand-ink hover:text-brand-rust transition-colors">{credit}</Link></span>
              {isCourse && <><span>·</span><span>{totalLessons} lessons</span></>}
              {!isCourse && p.pages && <><span>·</span><span>{p.pages} pages</span></>}
            </div>

            <div className="mt-8 flex items-end justify-between border-t border-brand-line pt-6">
              {isCourse ? (
                <>
                  <span className="font-serif text-4xl text-brand-ink" data-testid="product-price">${Number(p.price).toFixed(2)}</span>
                  <span className="overline text-brand-inkMuted">Lifetime access</span>
                </>
              ) : (
                <>
                  <span className="font-serif text-2xl text-brand-ink leading-tight">Available on Amazon</span>
                  <span className="overline text-brand-inkMuted">Hardcover / Kindle</span>
                </>
              )}
            </div>

            {/* Course → in-app purchase. Book → external Amazon purchase. */}
            {isCourse ? (
              owned ? (
                <button
                  data-testid="open-in-library-btn"
                  onClick={() => navigate(`/learn/${p.product_id}`)}
                  className="mt-6 w-full bg-brand-ink text-white hover:bg-black py-4 text-sm font-medium tracking-wide flex items-center justify-center gap-2 transition-colors"
                >
                  <PlayCircle className="w-4 h-4" />
                  Continue learning
                </button>
              ) : (
                <button
                  data-testid="add-to-cart-btn"
                  disabled={has(p.product_id)}
                  onClick={handleAdd}
                  className="mt-6 w-full bg-brand-ink text-white hover:bg-black py-4 text-sm font-medium tracking-wide flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                >
                  <ShoppingBag className="w-4 h-4" />
                  {has(p.product_id) ? "Already in cart" : "Add to cart"}
                </button>
              )
            ) : (              <a
                href={p.amazon_url || "#"}
                target="_blank"
                rel="noopener noreferrer sponsored"
                data-testid="amazon-buy-btn"
                className={`mt-6 w-full inline-flex items-center justify-center gap-2 py-4 text-sm font-medium tracking-wide transition-colors ${
                  p.amazon_url
                    ? "bg-brand-ink text-white hover:bg-black"
                    : "bg-brand-line text-brand-inkMuted cursor-not-allowed pointer-events-none"
                }`}
                onClick={(e) => { if (!p.amazon_url) e.preventDefault(); }}
              >
                Buy on Amazon
                <ExternalLink className="w-4 h-4" />
              </a>
            )}

            {/* Combo: book + companion course (books only, when both exist) */}
            {!isCourse && p.amazon_url && p.companion_course && (
              <button
                data-testid="buy-book-and-course-btn"
                onClick={() => {
                  // Add the companion course to cart (with the shape ProductCard / addItem expect)
                  addItem({
                    product_id: p.companion_course.product_id,
                    title: p.companion_course.title,
                    price: p.companion_course.price,
                    type: "course",
                    cover_image: p.companion_course.cover_image,
                    instructor: p.companion_course.instructor,
                  });
                  // Open Amazon in a new tab
                  window.open(p.amazon_url, "_blank", "noopener,noreferrer");
                  toast.success("Course added to cart — Amazon opened in a new tab");
                  // Send the user to the cart so they can complete the course checkout
                  navigate("/cart");
                }}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white py-3 text-sm font-medium tracking-wide transition-colors"
              >
                Buy book + companion course
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            )}

            {/* Free preview link — courses only, hide once enrolled */}
            {isCourse && !owned && (
              <Link
                to="/preview"
                data-testid="course-detail-free-preview"
                className="block mt-3 text-center text-sm text-brand-rust hover:underline py-2"
              >
                Or try Module 1 free →
              </Link>
            )}

            {/* 3-payment plan trigger — courses only, hide if enrolled */}
            {isCourse && !owned && (
              <button
                type="button"
                onClick={() => setShowPlan(true)}
                data-testid="payment-plan-trigger"
                className="block w-full text-center text-xs uppercase tracking-[.18em] text-brand-inkMuted hover:text-brand-ink py-2 mt-1"
              >
                Or 3 monthly payments of $349
              </button>
            )}

            <ul className="mt-6 space-y-3 text-sm text-brand-inkMuted">
              {isCourse ? (
                <>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-rust" /> Instant access after purchase</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-rust" /> All modules & lessons unlocked</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-rust" /> Lifetime updates</li>
                </>
              ) : (
                <>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-rust" /> Ships from Amazon</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-rust" /> Kindle, hardcover & paperback editions</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-rust" /> Pair with the companion course on this site</li>
                </>
              )}
            </ul>
          </div>
        </aside>
      </section>

      {/* Outcomes — courses only. Replaces the testimonials section until real quotes are collected. */}
      {isCourse && (
        <section className="bg-brand-bg border-t border-brand-line py-20 md:py-28" data-testid="outcomes-section">
          <div className="container-narrow">
            <span className="overline text-brand-rust">What you&apos;ll walk away with</span>
            <h2 className="font-display text-4xl md:text-5xl mt-3 text-brand-ink leading-tight max-w-3xl">
              Twelve modules. Forty-eight lessons. <em className="italic">Three shifts that stick.</em>
            </h2>
            <p className="mt-5 text-brand-inkMuted max-w-2xl leading-relaxed">
              This isn&apos;t a course you finish and forget. It&apos;s a deliberate set of drills, conversations, and decisions designed to change the way you operate, week by week, for the rest of your career.
            </p>

            <div className="grid md:grid-cols-3 gap-8 mt-14">
              {[
                {
                  k: "01",
                  title: "Decision clarity under pressure",
                  body: "Stop second-guessing the hard calls. You'll leave with a repeatable framework for the decisions you currently postpone, and the conversation scripts that make them survivable on both sides of the table.",
                },
                {
                  k: "02",
                  title: "A standard your team actually defends",
                  body: "Walk away with three written standards you'll hold without exception, the language to enforce them without bruising trust, and the weekly cadence that keeps them from drifting the moment the quarter heats up.",
                },
                {
                  k: "03",
                  title: "A leadership practice that compounds",
                  body: "Three weekly drills — a written Friday review, one uncomfortable conversation, one fast decision — that take ninety minutes a week and rebuild how your team experiences you over the next ninety days.",
                },
              ].map((o) => (
                <article
                  key={o.k}
                  data-testid={`outcome-${o.k}`}
                  className="bg-white border border-brand-line p-8 md:p-10 flex flex-col"
                >
                  <span className="font-display text-5xl text-brand-rust leading-none">{o.k}</span>
                  <h3 className="font-serif text-xl text-brand-ink mt-6 leading-snug">{o.title}</h3>
                  <p className="mt-4 text-brand-ink text-[15px] leading-relaxed flex-1">{o.body}</p>
                </article>
              ))}
            </div>

            <div className="mt-14 border-t border-brand-line pt-10 grid md:grid-cols-3 gap-6 text-sm">
              <div data-testid="outcome-spec-modules">
                <span className="overline text-brand-inkMuted">Structure</span>
                <p className="font-serif text-xl text-brand-ink mt-1">12 modules · 48 lessons</p>
              </div>
              <div data-testid="outcome-spec-time">
                <span className="overline text-brand-inkMuted">Time commitment</span>
                <p className="font-serif text-xl text-brand-ink mt-1">~25 hours of deliberate work</p>
              </div>
              <div data-testid="outcome-spec-certificate">
                <span className="overline text-brand-inkMuted">Earn the certificate</span>
                <p className="font-serif text-xl text-brand-ink mt-1">Pass the final at 80%</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 3-payment plan modal */}
      {showPlan && (
        <PaymentPlanModal
          courseTitle={p.title}
          onClose={() => setShowPlan(false)}
        />
      )}
    </Layout>
  );
}

function PaymentPlanModal({ courseTitle, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [loading, setLoading] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeRedirectUrl, setStripeRedirectUrl] = useState(null);
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/payment-plans/request", {
        ...form,
        course_title: courseTitle,
      });
      setDone(true);
      toast.success("Request received — Robbie will email you within 24h.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Try again.");
    } finally {
      setLoading(false);
    }
  }

  async function payWithStripe() {
    if (!form.name?.trim()) return toast.error("Add your name first");
    if (!form.email?.trim()) return toast.error("Add your email first");
    setStripeLoading(true);
    try {
      const { data } = await api.post("/checkout/3pay", {
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        note: form.note || undefined,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        // Show the "Sending you to Stripe..." overlay FIRST so React can paint
        // it before we navigate away. Without the small delay, the browser
        // leaves our page immediately and the user stares at a white screen
        // while Stripe loads (2-5s on slow connections, indefinitely if an
        // ad-blocker/VPN/extension blocks Stripe Checkout). The overlay
        // includes a manual fallback link in case auto-redirect fails.
        setStripeRedirectUrl(data.checkout_url);
        // 350ms is enough for one paint frame + a perceptible "going somewhere"
        // signal. Stripe's own checkout.redirectToCheckout uses ~250ms.
        setTimeout(() => {
          window.location.assign(data.checkout_url);
        }, 350);
      } else {
        toast.error("Could not start Stripe checkout. Try again.");
        setStripeLoading(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Stripe checkout unavailable. Try the manual request below.");
      setStripeLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-brand-ink/70 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="payment-plan-modal"
    >
      <div
        className="bg-white max-w-md w-full p-8 md:p-10 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="payment-plan-close"
          className="absolute top-4 right-4 text-brand-inkMuted hover:text-brand-ink text-2xl leading-none"
        >×</button>

        {stripeRedirectUrl ? (
          <div className="text-center py-10" data-testid="payment-plan-redirecting">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-brand-ink" />
            <p className="font-serif text-2xl mt-6 text-brand-ink">Sending you to Stripe…</p>
            <p className="text-brand-inkMuted mt-2 text-sm">This usually takes a few seconds.</p>
            <p className="text-brand-inkMuted mt-6 text-xs">
              Page didn&apos;t load?{" "}
              <a
                href={stripeRedirectUrl}
                rel="noopener noreferrer"
                className="text-brand-rust underline underline-offset-4 hover:text-brand-ink"
                data-testid="payment-plan-manual-redirect"
              >
                Click here to open Stripe Checkout &rarr;
              </a>
            </p>
          </div>
        ) : !done ? (
          <>
            <span className="overline text-brand-rust">3-payment plan</span>
            <h3 className="font-display text-3xl mt-3 text-brand-ink leading-tight">
              Three monthly payments of $349.
            </h3>
            <p className="mt-4 text-sm text-brand-inkMuted leading-relaxed">
              Pay $349 today via Stripe and you&apos;re in — instant course access. Stripe will charge the same card $349 a month for the next two months, then automatically stop. Total $1,047.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="payment-plan-form">
              <input
                required
                placeholder="Full name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-transparent border-b border-brand-line py-3 text-brand-ink focus:outline-none focus:border-brand-rust"
                data-testid="payment-plan-name"
              />
              <input
                required
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-transparent border-b border-brand-line py-3 text-brand-ink focus:outline-none focus:border-brand-rust"
                data-testid="payment-plan-email"
              />
              <input
                placeholder="Phone (optional)"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full bg-transparent border-b border-brand-line py-3 text-brand-ink focus:outline-none focus:border-brand-rust"
                data-testid="payment-plan-phone"
              />
              <textarea
                rows={3}
                placeholder="Anything I should know? (optional)"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="w-full bg-transparent border-b border-brand-line py-3 text-brand-ink focus:outline-none focus:border-brand-rust resize-none"
                data-testid="payment-plan-note"
              />

              <button
                type="button"
                onClick={payWithStripe}
                disabled={stripeLoading || loading}
                data-testid="payment-plan-pay-stripe"
                className="w-full bg-brand-rust text-white hover:bg-brand-ink px-7 py-4 text-sm font-medium tracking-wide transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {stripeLoading ? "Opening Stripe…" : "Pay $349 today with Stripe → instant access"}
              </button>

              <div className="flex items-center gap-4 py-2">
                <span className="flex-1 h-px bg-brand-line" />
                <span className="overline text-brand-inkMuted">or</span>
                <span className="flex-1 h-px bg-brand-line" />
              </div>

              <button
                type="submit"
                disabled={loading || stripeLoading}
                data-testid="payment-plan-submit"
                className="w-full bg-transparent border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white px-7 py-4 text-sm font-medium tracking-wide transition-colors disabled:opacity-60"
              >
                {loading ? "Sending…" : "Email me an invoice instead (24h reply)"}
              </button>

              <p className="text-xs text-brand-inkMuted text-center pt-1">
                Secure checkout by Stripe. Cards · Apple Pay · Google Pay.
              </p>
            </form>
          </>
        ) : (
          <div className="text-center" data-testid="payment-plan-done">
            <div className="w-12 h-12 mx-auto bg-brand-rust/15 text-brand-rust rounded-full flex items-center justify-center text-xl">✓</div>
            <h3 className="font-display text-2xl mt-5 text-brand-ink">Request received.</h3>
            <p className="mt-3 text-sm text-brand-inkMuted leading-relaxed">
              Robbie will email you the three Stripe invoices within 24 hours. Pay the first one, get immediate course access.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 text-sm text-brand-rust hover:underline"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
